import React from 'react'

import {
  ComparableProperty,
  Level,
  PropertyCompare,
  PropertyType,
  Stage,
} from '@adUtilities/types'

import useCompareProperty from './use-compare-property'

export interface ExtendedStage extends Stage {
  precinctId: string
}

interface UnitData {
  propertyType: PropertyType.Unit
  data: Level[]
}

interface LotData {
  propertyType: PropertyType.Lot
  data: ExtendedStage[]
}

type PropertyData = UnitData | LotData

type UseSyncComparablePropertiesFromSessionArgs = {
  disable?: boolean
  firebasePropertyCompare: PropertyCompare | undefined
  dataLoaded: boolean
  comparePropertyProps: ReturnType<typeof useCompareProperty>
} & PropertyData

const useSyncComparablePropertiesFromSession = ({
  disable,
  firebasePropertyCompare,
  propertyType,
  dataLoaded,
  data,
  comparePropertyProps,
}: UseSyncComparablePropertiesFromSessionArgs) => {
  const {
    compareModeIsActive,
    startComparison,
    setComparableProperties,
    stopComparison,
  } = comparePropertyProps

  const sessionPropertiesChangeTriggerKey = React.useMemo(() => {
    const sortedProperties = [
      ...(firebasePropertyCompare?.properties || []),
    ].sort((item) => item.index)
    const ids = sortedProperties.map((item) => item.propertyId)
    return ids.join('__')
  }, [firebasePropertyCompare?.properties])

  const dataChangeTriggerKey = React.useMemo(() => {
    let ids: string[] = []
    if (propertyType === PropertyType.Unit) {
      ids = data.map((item) => item.level)
    }
    if (propertyType === PropertyType.Lot) {
      ids = data.map((item) => item.id)
    }
    return ids.join('__')
  }, [data, propertyType])

  const generateComparableUnits = React.useCallback<
    () => ComparableProperty[]
  >(() => {
    if (!(firebasePropertyCompare && propertyType === PropertyType.Unit)) {
      return []
    }
    const comparableUnits =
      firebasePropertyCompare.properties.map<ComparableProperty>((item) => {
        if (item.propertyType !== PropertyType.Unit) {
          return {
            index: item.index,
            property: null,
          }
        }
        const matchedLevel = data.find(
          (level) => level.level === item.references.levelId
        )
        const matchedUnit = matchedLevel?.data.find(
          (unit) => unit.name === item.propertyId
        )
        return {
          index: item.index,
          property: matchedUnit
            ? {
                data: matchedUnit,
                propertyType: item.propertyType,
                references: item.references,
              }
            : null,
        }
      })
    return comparableUnits
  }, [firebasePropertyCompare?.properties, propertyType, data])

  const generateComparableLots = React.useCallback<
    () => ComparableProperty[]
  >(() => {
    if (!(firebasePropertyCompare && propertyType === PropertyType.Lot)) {
      return []
    }
    const comparableLots =
      firebasePropertyCompare.properties.map<ComparableProperty>((item) => {
        if (item.propertyType !== PropertyType.Lot) {
          return {
            index: item.index,
            property: null,
          }
        }
        const matchedStage = data.find(
          (stage) =>
            stage.precinctId === item.references.precinctId &&
            stage.label === item.references.stageId
        )
        const matchedLot = matchedStage?.lots.find(
          (lot) => lot.name === item.propertyId
        )
        return {
          index: item.index,
          property: matchedLot
            ? {
                data: matchedLot,
                propertyType: item.propertyType,
                references: item.references,
              }
            : null,
        }
      })
    return comparableLots
  }, [firebasePropertyCompare?.properties, propertyType, data])

  const syncStateWithSession = React.useCallback(() => {
    if (firebasePropertyCompare?.active) {
      if (!compareModeIsActive) {
        startComparison()
      }
      if (propertyType === PropertyType.Unit) {
        setComparableProperties(generateComparableUnits())
      }
      if (propertyType === PropertyType.Lot) {
        setComparableProperties(generateComparableLots())
      }
      return
    }
    stopComparison()
  }, [
    firebasePropertyCompare?.active,
    compareModeIsActive,
    startComparison,
    propertyType,
    setComparableProperties,
    generateComparableUnits,
    generateComparableLots,
    stopComparison,
  ])

  React.useEffect(() => {
    if (disable || !dataLoaded) {
      return
    }
    syncStateWithSession()
  }, [
    disable,
    sessionPropertiesChangeTriggerKey,
    dataChangeTriggerKey,
    firebasePropertyCompare?.active,
    dataLoaded,
  ])

  return {
    syncStateWithSession,
  }
}

export default useSyncComparablePropertiesFromSession
